import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { getSsoTestResult } from "../../../services/SsoService";
import { APP_ROUTE_ENTERPRISE } from "../../../constants/RouteConstants";
import Link from "../../common/link";
import "./sso-test-result.scss";

// The reasons the API records for a test that matched nobody, as sentences.
const PROBLEM_MESSAGES = {
  "Not found": "sso_test_problem_not_found",
  "Not an SSO user": "sso_test_problem_not_sso_user",
  "Account inactive": "sso_test_problem_inactive",
  "Signature not valid": "sso_test_problem_signature",
  "Certificate expired": "sso_test_problem_certificate_expired",
  "Response expired": "sso_test_problem_response_expired",
  "Response already used": "sso_test_problem_response_used",
  "Meant for another site": "sso_test_problem_wrong_site",
  "Company sign-in error": "sso_test_problem_provider_error",
  "Email not verified by the company sign-in service": "sso_test_problem_email_not_verified",
  "Client secret could not be read": "sso_test_problem_secret_unreadable",
};

// The rule that accepted or refused an OIDC email, as sentences.
const EMAIL_CHECK_MESSAGES = {
  verified: "sso_test_email_verified",
  "microsoft-verified-domain": "sso_test_email_microsoft_verified_domain",
  "trusted-provider": "sso_test_email_trusted_provider",
  "not-verified": "sso_test_email_not_verified",
};

// A received value, broken anywhere so a long claim URI wraps instead of
// widening the table.
const wrapping = (field) => (row) => <span className="sso-test-value">{row[field]}</span>;

// /enterprise/sso-test?code=...: what a Test connection run received from
// the company and whom it matched. Nobody was signed in.
export const SsoTestResult = ({ subDomain }) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [result, setResult] = useState(null);
  const [expired, setExpired] = useState(false);
  const message = (id, values) => intl.formatMessage({ id }, values);

  useEffect(() => {
    if (!code) {
      setExpired(true);
      return undefined;
    }
    if (!subDomain) {
      return undefined;
    }
    let cancelled = false;
    getSsoTestResult(subDomain, code)
      .then((ajax) => {
        if (!cancelled) {
          setResult(ajax.data?.response || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExpired(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, subDomain]);

  const problemText = (problem) =>
    PROBLEM_MESSAGES[problem]
      ? message(PROBLEM_MESSAGES[problem])
      : message("sso_test_not_matched", { problem: problem || "" });

  const matchedText = (user) =>
    user.email
      ? message("sso_test_matched", { name: user.name, email: user.email })
      : message("sso_test_matched_no_email", { name: user.name });

  const rows = result
    ? [
        ...(result.nameId ? [{ type: "NameID", value: result.nameId }] : []),
        ...(result.claims || []),
      ]
    : [];

  return (
    <div className="edexlms-page-content sso-test-result">
      <div className="edexlms-page-header">
        <div className="edexlms-page-title">{message("sso_test_title")}</div>
      </div>
      <div className="edexlms-page-body flex flex-column gap-3">
        {(expired && (
          <div className="flex flex-column align-items-start gap-2">
            <Message
              severity="warn"
              className="edexlms-readable-message"
              text={message("sso_test_expired")}
            />
            <Link to={`${APP_ROUTE_ENTERPRISE}?tab=company-sign-in`}>
              {message("sso_test_back")}
            </Link>
          </div>
        )) ||
          null}
        {(!expired && !result && <ProgressSpinner style={{ width: "40px", height: "40px" }} />) || null}
        {(result && (
          <>
            <div className="text-600">{message("sso_test_note")}</div>
            {(result.matchedUser && (
              <Message
                severity="success"
                className="edexlms-readable-message"
                text={matchedText(result.matchedUser)}
              />
            )) || (
              <Message
                severity="error"
                className="edexlms-readable-message"
                text={problemText(result.problem)}
              />
            )}
            {(EMAIL_CHECK_MESSAGES[result.emailCheck] && (
              <div className="text-600" data-testid="sso-test-email-check">
                {message(EMAIL_CHECK_MESSAGES[result.emailCheck])}
              </div>
            )) ||
              null}
            {(rows.length > 0 && (
              <div>
                <div className="font-semibold mb-2">{message("sso_test_received")}</div>
                <DataTable
                  value={rows}
                  size="small"
                  stripedRows
                  responsiveLayout="stack"
                  breakpoint="640px"
                  className="sso-test-claims"
                >
                  <Column field="type" header={message("sso_test_claim")} body={wrapping("type")} />
                  <Column field="value" header={message("sso_test_value")} body={wrapping("value")} />
                </DataTable>
              </div>
            )) ||
              null}
          </>
        )) ||
          null}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  subDomain: state.global?.enterprise?.subDomain,
});

export default connect(mapStateToProps)(SsoTestResult);
